import { runInAction } from 'mobx';
import AppStore from '@app/src/common/store/AppStore';
import {
  FormLayout,
  IDictionary,
  IFormConfiguration,
  ITranslations
} from '@app/src/common/model/FormConfiguration';
import localeStore from '@app/src/common/store/LocaleStore';
import {
  ConditionType,
  DependenceRule,
  DependenceType,
  IDependence,
  IFormField,
  ISourceDependence,
  PrefillType,
  RestrictionCondition,
  RestrictionType,
  SourceType
} from '@app/src/common/model/FormField';
import { ITableConfiguration } from '@app/src/common/model/TableConfiguration';
import AuthStore from '@app/src/common/store/AuthStore';
import DynamicForm from './DynamicForm';
import { FormControlType } from '@app/src/common/model/FormControls';
import { FieldType } from '@app/src/common/components/form/MobxFieldControl';
import { reloadDictionary } from '@app/src/common/utils/DictionaryUtils';
import { resolveDictionaryKey } from './dictionaryKey';

export function getFormConfig(
  configKey: string,
  defaultFormConfig: IFormField[],
  returnDefault: boolean = true
) {
  const serverConfig = AppStore.configs.get(configKey);
  if (serverConfig) {
    const config: IFormConfiguration = JSON.parse(serverConfig);
    addTranslations(config.translations);
    const form = config.forms ? config.forms[0] : null;
    if (!form) {
      return defaultFormConfig;
    } else if (form.extendDefault) {
      return defaultFormConfig.concat(form.fields);
    }
    const fields: IFormField[] = [];
    form.fields.forEach(field => {
      if (field.type === FieldType.STANDARD) {
        const standardField = defaultFormConfig.find(
          item => field.name === item.name
        );
        if (standardField) {
          fields.push(standardField);
        }
      } else {
        fields.push(field);
      }
    });
    return fields;
  } else {
    return returnDefault ? defaultFormConfig : null;
  }
}

function findForm(config: IFormConfiguration, type?: string) {
  if (!config.forms) {
    return null;
  }
  if (!type) {
    return config.forms[0];
  }
  let formForType = config.forms.find(item => item.type === type);
  if (!formForType) {
    formForType = config.forms.find(item => item.defaultType);
  }
  return formForType;
}

export function getEventConfig(
  configKey: string,
  defaultFormConfig: IFormField[],
  returnDefault: boolean = true,
  type?: string
) {
  const serverConfig = AppStore.configs.get(configKey);
  if (serverConfig) {
    const config: IFormConfiguration = JSON.parse(serverConfig);
    addTranslations(config.translations);
    const form = findForm(config, type);
    const defaultConfig = Array.from(defaultFormConfig);
    const fields = form
      ? form.extendDefault
        ? defaultConfig.concat(form.fields)
        : form.fields
      : defaultConfig;
    if (!form?.type && fields[0].name === 'typeHeader') {
      fields.shift();
    }
    const fullConfig = {
      fields,
      layout: form?.layout || FormLayout.FORM,
      type: form?.type,
      visitorList: null
    };
    fullConfig.visitorList = form
      ? (form as any).visitorList
      : (config as any).visitorList || null;
    return fullConfig;
  } else {
    return returnDefault
      ? { fields: defaultFormConfig, layout: FormLayout.FORM, type: undefined }
      : null;
  }
}

export function hasConfig(configKey: string) {
  return !!AppStore.configs.get(configKey);
}

export function addTranslations(translations: ITranslations) {
  if (translations) {
    localeStore.extendTranslations(translations[localeStore.locale] || {});
  }
}

export function getTableConfig(configKey: string) {
  const serverConfig = AppStore.configs.get(configKey);
  if (serverConfig) {
    const config: ITableConfiguration = JSON.parse(serverConfig);
    addTranslations(config.translations);
    return config.table || null;
  } else {
    return null;
  }
}

export function getFullTableConfig(configKey: string) {
  const serverConfig = AppStore.configs.get(configKey);
  if (serverConfig) {
    const config: ITableConfiguration = JSON.parse(serverConfig);
    addTranslations(config.translations);
    return {
      table: config.table || null,
      details: config.details || null,
      dictionaries: config.dictionaries || null,
      dialog: config.dialog || null,
      visitTypes: config.visitTypes || null
    };
  } else {
    return null;
  }
}

export function hasRestriction(field: IFormField) {
  const { restriction } = field;
  if (restriction) {
    const { role, condition } = restriction;
    const hasRole = AuthStore.userRoles.includes(role);
    if (condition === RestrictionCondition.INCLUDE && !hasRole) {
      return true;
    }
    if (condition === RestrictionCondition.EXCLUDE && hasRole) {
      return true;
    }
  }
  return false;
}

export function checkHideDependencyOrRestriction(
  field: IFormField,
  form: DynamicForm
) {
  const { dependOn, restriction } = field;
  if (dependOn) {
    const { value, type, rule = DependenceRule.EQUAL, condition } = dependOn;
    const dependValue = form[dependOn.field]?.value;
    if (type === DependenceType.HIDE) {
      if (condition === ConditionType.EMPTY && !dependValue) {
        return true;
      } else if (rule === DependenceRule.EQUAL && dependValue === value) {
        return true;
      } else if (rule === DependenceRule.NOT_EQUAL && dependValue !== value) {
        return true;
      }
    }
  }
  return restriction?.type === RestrictionType.VIEW && hasRestriction(field);
}

export function checkDisableDependency(
  { dependOn }: IFormField,
  form: DynamicForm[],
  isNew = false
) {
  if (dependOn?.type !== DependenceType.DISABLE) {
    return false;
  }

  const {
    field: fieldName,
    value,
    rule,
    condition,
    excludedValue,
    excludedWeekDays
  } = dependOn;

  if (condition === ConditionType.EDIT) {
    return !isNew;
  }

  const formField =
    form.length === 1
      ? form[fieldName]
      : form.find(item => item.hasField(fieldName))?.getControl(fieldName);
  if (
    condition === ConditionType.TIME_PERIOD &&
    (formField?._fieldType as FieldType) === FieldType.DATE
  ) {
    const dependValue = formField?.value;
    if (!dependValue) {
      return true;
    }
    const targetDate = new Date(dependValue);
    const period = calculatePeriod(
      value,
      targetDate,
      excludedValue,
      excludedWeekDays
    );
    switch (rule) {
      case DependenceRule.LESS:
        return targetDate.getTime() - Date.now() < period;
      case DependenceRule.NOT_EQUAL:
        return targetDate.getTime() - Date.now() !== period;
      case DependenceRule.EQUAL:
        return targetDate.getTime() - Date.now() === period;
    }
  }
  return false;
}

export function calculatePeriod(
  hours: number,
  date: Date,
  excludedValue?: string[],
  excludedWeekDays?: number[]
) {
  const hour = 1000 * 60 * 60;
  let period = hours * hour;
  if (
    date.getTime() < Date.now() + period ||
    (!excludedValue && !excludedWeekDays)
  ) {
    return period;
  }
  const formatter = new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric'
  });
  let next = true;
  while (next) {
    const nextDate = new Date(Date.now() + period);
    if (excludedWeekDays?.includes(nextDate.getDay())) {
      period += hours * hour;
    } else if (excludedValue?.includes(formatter.format(nextDate))) {
      period += hours * hour;
    } else {
      next = false;
    }

    if (nextDate.getTime() > date.getTime()) {
      next = false;
    }
  }

  return period;
}

export function checkRequired(
  dependOn: IDependence | undefined,
  form: DynamicForm
) {
  let required = false;
  if (dependOn) {
    const dependValue = form[dependOn.field]?.value;
    if (
      dependOn.type === DependenceType.REQUIRED &&
      dependValue === dependOn.value
    ) {
      required = true;
    }
  }
  return required;
}

export function extendFieldProps(
  field: IFormField,
  dictionaries?: IDictionary
) {
  const props = { ...(field.props || {}) };
  if (field.sourceName) {
    let sourceName = field.sourceName;
    if (sourceName.startsWith('dictionary')) {
      const [_, key] = sourceName.split(':');
      sourceName = key;
    }
    if (field.control === FormControlType.MULTISELECT) {
      props.options = dictionaries?.[sourceName] || [];
    } else if (field.control === FormControlType.HEADER) {
      const value = dictionaries?.[sourceName];
      props.values = value ? ({ [sourceName]: value } as any) : undefined;
    } else {
      props.items = dictionaries?.[sourceName] || [];
    }
  }
  return props;
}

export function prefillForm(
  form: DynamicForm,
  dictionaries?: IDictionary,
  skipTouched: boolean = false
) {
  form.formFields.forEach(field => {
    if (field.prefill) {
      prefillField(field, form, dictionaries, skipTouched);
    }
  });
}

export function startEndDates() {
  const currentDate = new Date();
  const minutes = currentDate.getMinutes() < 30 ? 30 : 60;
  currentDate.setMinutes(minutes, 0, 0);
  const eventStart = currentDate.toISOString();
  currentDate.setMinutes(currentDate.getMinutes() + 30);
  const eventEnd = currentDate.toISOString();
  return { eventStart, eventEnd };
}

export function prefillField(
  field: IFormField,
  form: DynamicForm,
  dictionaries?: IDictionary,
  skipTouched: boolean = false
) {
  if (
    !field.prefill ||
    ![PrefillType.CURRENT_USER, PrefillType.OFFICE_USER].includes(
      field.prefill.type
    )
  ) {
    return;
  }

  if (skipTouched && form.getControl(field.name)?.isTouched) {
    return;
  }

  if (!field.prefill.sourceName && !field.prefill.matchField) {
    prefillFromUser(field, form);
    return;
  }

  prefillFromDictionary(field, form, dictionaries);
}

function prefillFromUser(field: IFormField, form: DynamicForm) {
  let value = AppStore.user![field.prefill!.field];
  if (field.prefill!.field === 'fullName') {
    value = AppStore.fullName;
  }
  if (value) {
    form.init({ [field.name]: value });
  }
}

function prefillFromDictionary(
  field: IFormField,
  form: DynamicForm,
  dictionaries?: IDictionary
) {
  if (!field.prefill?.sourceName || !dictionaries?.[field.prefill.sourceName]) {
    return;
  }
  const matchValue = AppStore.user![field.prefill!.matchField!];
  const matchedEntity = dictionaries[field.prefill.sourceName!].find(item =>
    matchesPrefillItem(item, field.prefill!.matchField!, matchValue)
  );
  if (!matchedEntity) {
    return;
  }
  const value = matchedEntity.option
    ? matchedEntity.option[field.prefill.field]
    : field.prefill.field === 'id'
      ? matchedEntity.value
      : undefined;
  if (value) {
    form.init({ [field.name]: value });
  }
}

function matchesPrefillItem(
  item: any,
  matchField: string,
  matchValue: any
): boolean {
  const normalizedMatch = matchValue?.toString().toLowerCase();
  if (matchField === 'id') {
    return item.value.toString().toLowerCase() === normalizedMatch;
  }
  const optionValue = item.option?.[matchField];
  return optionValue
    ? optionValue.toString().toLowerCase() === normalizedMatch
    : false;
}

export function extractDictionaries(
  fields: IFormField[],
  withDependentDictionaries: boolean
) {
  const dictionaries: string[] = [];
  fields.forEach(field => {
    const dependentCheck = withDependentDictionaries || !field.sourceDependOn;
    if (
      field.sourceType === SourceType.DB &&
      field.sourceName &&
      dependentCheck
    ) {
      dictionaries.push(field.sourceName);
    }
  });
  return dictionaries;
}

export function getFormDictionaries(
  configKey: string,
  withDependentDictionaries = false
) {
  const serverConfig = AppStore.configs.get(configKey);
  if (!serverConfig) {
    return null;
  }
  const config: IFormConfiguration = JSON.parse(serverConfig);
  const dictionaries: string[] = [];
  config.forms?.forEach(form => {
    if (form.layout) {
      form.fields.forEach(step => {
        if (step.control === FormControlType.STEP) {
          dictionaries.push(
            ...extractDictionaries(step.fields!, withDependentDictionaries)
          );
        }
      });
    } else {
      dictionaries.push(
        ...extractDictionaries(form.fields, withDependentDictionaries)
      );
    }
  });
  return Array.from(new Set(dictionaries));
}

export function extractDictionariesWithDependencies(fields: IFormField[]) {
  const dictionaries = new Map<string, ISourceDependence>();
  fields.forEach(({ sourceName, sourceType, sourceDependOn }) => {
    if (sourceType === SourceType.DB && sourceName && sourceDependOn) {
      dictionaries.set(sourceName, sourceDependOn);
    }
  });
  return dictionaries;
}

export function getFormDictionariesWithDependency(configKey: string) {
  const serverConfig = AppStore.configs.get(configKey);
  if (!serverConfig) {
    return null;
  }
  const config: IFormConfiguration = JSON.parse(serverConfig);
  let dictionaries = new Map<string, ISourceDependence>();
  config.forms?.forEach(form => {
    if (form.layout) {
      form.fields.forEach(step => {
        if (step.control === FormControlType.STEP) {
          dictionaries = new Map([
            ...dictionaries,
            ...extractDictionariesWithDependencies(step.fields!)
          ]);
        }
      });
    } else {
      dictionaries = new Map([
        ...dictionaries,
        ...extractDictionariesWithDependencies(form.fields)
      ]);
    }
  });
  return Object.fromEntries(dictionaries);
}

function mergeDictionaries(dictionaries: IDictionary, update: IDictionary) {
  runInAction(() => Object.assign(dictionaries, update));
}

function clearDictionary(dictionaries: IDictionary, key: string) {
  runInAction(() => {
    dictionaries[key] = [];
  });
}

export function setupSourceDependencies(
  forms: DynamicForm[],
  getDictionaries: () => IDictionary
) {
  const allFields = forms.flatMap(form => form.formFields);
  const dependentFields = allFields.filter(
    field => field.sourceName && field.sourceDependOn
  );

  dependentFields.forEach(field => {
    const { sourceDependOn, sourceName } = field;
    const sourceForm = forms.find(f => f.hasField(sourceDependOn!.field));
    const sourceControl = sourceForm?.getControl(sourceDependOn!.field);

    const dependentForm = forms.find(f => f.hasField(field.name));
    const dependentControl = dependentForm?.getControl(field.name);

    if (!sourceControl || !dependentControl) {
      return;
    }

    const existingHandler = sourceControl._onChanges;
    const dictionaryKey = resolveDictionaryKey(sourceName!);

    sourceControl.onChanges.subscribe((value: any, oldValue: any) => {
      if (existingHandler) {
        existingHandler(value, oldValue);
      }
      if (value === oldValue) {
        return;
      }
      dependentControl.setValue(undefined);
      if (value !== undefined && value !== null && value !== '') {
        reloadDictionary(sourceName!, {
          [sourceDependOn!.paramName]: value
        }).then(result => mergeDictionaries(getDictionaries(), result));
      } else {
        clearDictionary(getDictionaries(), dictionaryKey);
      }
    });
  });
}
